// Master-P3 Wave 3.1 — Research-Zugänge tab.
//
// Rendered inside GuidedIntegrationCenter as the "Recherche-Zugänge"
// tab. Mirrors AdChannelsTab shape:
//   * Provider catalog with capability chips.
//   * Connect flow via `/research-providers/{provider}/connect`.
//   * Live search-ads and discover-trends demo (mock only).
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { Alert, AlertDescription, AlertTitle }
  from "../../components/ui/alert";
import {
  Search, Sparkles, PlugZap, CheckCircle2, Info, Radar,
} from "lucide-react";

const PROVIDER_META = {
  meta_ad_library: {
    label: "Meta Ad Library",
    description: (
      "Öffentliches Ad-Archiv (Meta / Facebook / Instagram). DSA-basierte " +
      "EU-Coverage seit 2023. Perfekt für Wettbewerbs-Ad-Analyse."),
  },
  apify: {
    label: "Apify",
    description: (
      "Web-Scraping-Platform mit vorgefertigten Actors für Trend- und " +
      "Content-Discovery. Verbraucht Actor-Compute-Credits pro Query."),
  },
};

function CapabilityRow({ cap, providerKey }) {
  const declaredLabel = cap.declared ? "deklariert" : "nicht deklariert";
  const verifiedLabel = cap.verified === null ? "nicht geprüft"
    : cap.verified ? "verifiziert" : "verifiziert fehlgeschlagen";
  const verifiedTone = cap.verified === null
    ? "bg-muted text-muted-foreground"
    : cap.verified ? "bg-emerald-100 text-emerald-800"
      : "bg-red-100 text-red-800";
  return (
    <div className="flex items-center justify-between gap-3
                    border-t py-2 text-sm"
         data-testid={`research-cap-row-${providerKey}-${cap.name}`}>
      <div className="flex-1">
        <div className="font-medium">{cap.name}</div>
        {cap.note && (
          <div className="text-xs text-muted-foreground mt-0.5">
            {cap.note}
          </div>
        )}
        {cap.requires_review && (
          <div className="text-xs text-amber-800 mt-0.5"
               data-testid={`research-cap-review-${providerKey}-${cap.name}`}>
            Freigabe erforderlich: {cap.requires_review}
          </div>
        )}
      </div>
      <div className="flex gap-1 shrink-0">
        <Badge className="bg-sky-100 text-sky-800 border-0"
               data-testid={`research-cap-declared-${providerKey}-${cap.name}`}>
          {declaredLabel}
        </Badge>
        <Badge className={`${verifiedTone} border-0`}
               data-testid={`research-cap-verified-${providerKey}-${cap.name}`}>
          {verifiedLabel}
        </Badge>
      </div>
    </div>
  );
}

function ConnectDialog({ provider, open, onOpenChange, onDone }) {
  const [accountRef, setAccountRef] = useState("");
  const [tokenBlob, setTokenBlob] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      let tokens = null;
      if (tokenBlob.trim()) {
        tokens = { access_token: tokenBlob.trim() };
      }
      const r = await api.post(
        `/research-providers/${provider.key}/connect`,
        { account_ref: accountRef || `mock_${provider.key}_account`,
          oauth_tokens: tokens,
          use_real_api: false });
      toast.success(`${provider.label} verbunden (Mock).`);
      onDone && onDone(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(
        `Verbinden fehlgeschlagen: ${e.response?.data?.detail?.error
                                       || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`research-connect-dialog-${provider.key}`}>
        <DialogHeader>
          <DialogTitle>{provider.label} verbinden</DialogTitle>
          <DialogDescription>
            Preview-Modus: Token wird verschlüsselt gespeichert, aber
            kein Realverkehr ausgelöst.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="account-ref">Account-Referenz</Label>
            <Input id="account-ref" value={accountRef}
                   onChange={(e) => setAccountRef(e.target.value)}
                   placeholder={`z.B. ${provider.key === "apify"
                                          ? "brimo_actor_workspace"
                                          : "meta_app_v26"}`}
                   data-testid={`input-account-ref-${provider.key}`} />
          </div>
          <div>
            <Label htmlFor="token">API-Token (optional; verschlüsselt)</Label>
            <Input id="token" value={tokenBlob} type="password"
                   onChange={(e) => setTokenBlob(e.target.value)}
                   placeholder="wird AES-256-GCM verschlüsselt"
                   data-testid={`input-token-${provider.key}`} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline"
                  onClick={() => onOpenChange(false)}
                  data-testid={`btn-cancel-connect-${provider.key}`}>
            Abbrechen
          </Button>
          <Button onClick={save} disabled={saving}
                  data-testid={`btn-save-connect-${provider.key}`}>
            {saving ? "Speichere…" : "Verbinden"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DemoPanel({ provider, connection }) {
  const [query, setQuery] = useState("Immobilien Wuppertal");
  const [ads, setAds] = useState(null);
  const [trends, setTrends] = useState(null);
  const [busy, setBusy] = useState(false);

  const runAds = async () => {
    setBusy(true); setAds(null);
    try {
      const r = await api.post(
        `/research-providers/${provider.key}/search-ads`,
        { query, regions: ["DE"], limit: 5 });
      setAds(r.data);
    } catch (e) {
      toast.error(`Fehler: ${e.response?.data?.detail?.error || e.message}`);
    } finally { setBusy(false); }
  };

  const runTrends = async () => {
    setBusy(true); setTrends(null);
    try {
      const r = await api.post(
        `/research-providers/${provider.key}/discover-trends`,
        { seed_terms: query.split(/[,;\s]+/).filter(Boolean).slice(0, 5),
          regions: ["DE"], limit: 6 });
      setTrends(r.data);
    } catch (e) {
      toast.error(`Fehler: ${e.response?.data?.detail?.error || e.message}`);
    } finally { setBusy(false); }
  };

  if (!connection) {
    return (
      <p className="text-sm text-muted-foreground"
         data-testid={`research-demo-locked-${provider.key}`}>
        Zuerst verbinden, um Test-Abfragen zu starten.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid={`research-demo-${provider.key}`}>
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <Label htmlFor={`q-${provider.key}`}>Test-Abfrage</Label>
          <Input id={`q-${provider.key}`} value={query}
                 onChange={(e) => setQuery(e.target.value)}
                 data-testid={`input-query-${provider.key}`} />
        </div>
        <Button size="sm" onClick={runAds} disabled={busy}
                data-testid={`btn-search-ads-${provider.key}`}>
          <Search className="h-3 w-3 mr-1" />
          Ads suchen
        </Button>
        {provider.supports_trend_discovery && (
          <Button size="sm" variant="outline"
                  onClick={runTrends} disabled={busy}
                  data-testid={`btn-discover-trends-${provider.key}`}>
            <Radar className="h-3 w-3 mr-1" />
            Trends
          </Button>
        )}
      </div>
      {ads && (
        <div className="rounded-md border p-3"
             data-testid={`ads-results-${provider.key}`}>
          <div className="text-xs text-muted-foreground mb-2">
            {ads.ads?.length || 0} Ads — {ads.mocked && "mock — "}
            {ads.fetched_at?.slice(0, 19)}Z
          </div>
          {(ads.ads || []).slice(0, 5).map((a, idx) => (
            <div key={a.external_id || idx}
                 className="border-t py-2 text-sm"
                 data-testid={`ad-item-${provider.key}-${idx}`}>
              <div className="font-medium">{a.advertiser_name}</div>
              <div className="text-xs">{a.headline}</div>
              <div className="text-xs text-muted-foreground">
                {a.body} — {a.impressions_range && `Impr: ${a.impressions_range} • `}
                {a.spend_range && `Spend: ${a.spend_range}`}
              </div>
            </div>
          ))}
        </div>
      )}
      {trends && (
        <div className="rounded-md border p-3"
             data-testid={`trends-results-${provider.key}`}>
          <div className="text-xs text-muted-foreground mb-2">
            {trends.trends?.length || 0} Trends — mock
          </div>
          <div className="flex flex-wrap gap-1">
            {(trends.trends || []).map((t, idx) => (
              <Badge key={idx} className="bg-fuchsia-100 text-fuchsia-800
                                            border-0"
                     data-testid={`trend-tag-${provider.key}-${idx}`}>
                #{t.value} · {(t.score * 100).toFixed(0)}%
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProviderCard({ provider, connections, onConnect, onRefresh }) {
  const [caps, setCaps] = useState(null);
  useEffect(() => {
    api.get(`/research-providers/${provider.provider}/capabilities`)
      .then((r) => setCaps(r.data.capabilities || []))
      .catch(() => setCaps([]));
  }, [provider.provider]);

  const meta = PROVIDER_META[provider.provider] || {
    label: provider.display_name, description: "" };
  const hasConn = (connections || []).length > 0;
  const activeConn = (connections || [])[0];

  return (
    <Card data-testid={`research-provider-card-${provider.provider}`}>
      <CardHeader className="flex flex-row items-start justify-between
                              gap-4 space-y-0">
        <div>
          <CardTitle>{meta.label}</CardTitle>
          <CardDescription className="mt-1">
            {meta.description}
            <div className="text-xs mt-2 flex flex-wrap gap-2">
              <span>Rate-Limit: {provider.capabilities.rate_limit_note}</span>
              <span>Kosten/Query: {" "}
                €{provider.capabilities.estimated_cost_per_query_usd.toFixed(2)}
              </span>
            </div>
          </CardDescription>
        </div>
        {hasConn ? (
          <Badge className="bg-emerald-100 text-emerald-800 border-0"
                 data-testid={`badge-connected-${provider.provider}`}>
            <CheckCircle2 className="h-3 w-3 mr-1 inline" />
            verbunden
          </Badge>
        ) : (
          <Button size="sm" onClick={() => onConnect(provider)}
                  data-testid={`btn-connect-${provider.provider}`}>
            <PlugZap className="h-3 w-3 mr-1" />
            Verbinden
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div data-testid={`research-capabilities-${provider.provider}`}>
          {caps === null && (
            <p className="text-sm text-muted-foreground">Lade Fähigkeiten…</p>
          )}
          {caps && caps.map((c) => (
            <CapabilityRow key={c.name} cap={c}
                             providerKey={provider.provider} />
          ))}
        </div>
        <DemoPanel provider={{
                     key: provider.provider,
                     supports_trend_discovery:
                       provider.capabilities.supports_trend_discovery,
                   }}
                     connection={activeConn} />
      </CardContent>
    </Card>
  );
}

export default function ResearchProvidersTab() {
  const [state, setState] = useState({ providers: [],
                                        real_apis_enabled: false });
  const [loading, setLoading] = useState(true);
  const [dialogProvider, setDialogProvider] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/research-providers")
      .then((r) => setState(r.data))
      .catch(() => setState({ providers: [], real_apis_enabled: false }))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <div className="space-y-6" data-testid="research-providers-tab">
      <div>
        <h2 className="text-xl font-semibold">Recherche-Zugänge</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-xl">
          Meta Ad Library und Apify liefern Wettbewerbs- und
          Trend-Signale. Preview-Umgebung: Adapter laufen im Mock-Modus,
          deterministisch pro Tenant + Query.
        </p>
      </div>

      {!state.real_apis_enabled && (
        <Alert data-testid="research-mock-mode-banner">
          <Sparkles className="h-4 w-4" />
          <AlertTitle>Mock-Modus aktiv</AlertTitle>
          <AlertDescription>
            <code className="text-xs">RESEARCH_PROVIDERS_REAL_APIS_ENABLED
            </code>{" "}ist deaktiviert. Alle Suchen liefern seeded
            Mock-Daten. Realverkehr wird erst freigeschaltet, wenn
            der Betreiber die Env-Variable setzt UND der Adapter den
            realen Pfad ausrollt.
          </AlertDescription>
        </Alert>
      )}

      {loading && <p className="text-sm">Wird geladen…</p>}
      {!loading && state.providers.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Keine Provider registriert.
        </p>
      )}

      <div className="space-y-4">
        {(state.providers || []).map((p) => (
          <ProviderCard key={p.provider} provider={p}
                          connections={p.connections}
                          onConnect={setDialogProvider}
                          onRefresh={load} />
        ))}
      </div>

      {dialogProvider && (
        <ConnectDialog
          provider={{ key: dialogProvider.provider,
                      label: PROVIDER_META[dialogProvider.provider]?.label
                               || dialogProvider.display_name }}
          open={!!dialogProvider}
          onOpenChange={(v) => !v && setDialogProvider(null)}
          onDone={() => load()} />
      )}
    </div>
  );
}
